import { FilterListFilled } from '@project-lary/react-material-symbols';
import { useGetCurrentUser } from 'gcs-common/clients/api/entities/current-user/current-user-api';
import { useGetColleaguesQuery } from 'gcs-common/clients/api/entities/tickets/tickets-api';
import {
  type FetchETSTicketsColleaguesResponseData,
  useETSTicketsQueryParams,
} from 'gcs-common/clients/api/entities/tickets/tickets-api-helpers';
import IconButton from 'gcs-common/design-system/ui/icon-button';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import LoadingIndicator from '../../../components/LoadingIndicator/LoadingIndicator';
import { Badge } from '../../../design-system/ui/badge';
import { Button, ButtonSize, ButtonVariant } from '../../../design-system/ui/button';
import { Checkbox } from '../../../design-system/ui/checkbox';
import NotificationBadge from '../../../design-system/ui/notification-badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../../design-system/ui/popover';
import { Separator } from '../../../design-system/ui/separator';
import { getCurrentUserId } from '../../../slices/currentUser/currentUserSelectors';

interface RowProps {
  colleague: FetchETSTicketsColleaguesResponseData['data'][number];
  onClick: (colleagueId: string) => void;
  checked: boolean;
}

const Row = (props: RowProps) => {
  const { colleague, onClick, checked } = props;
  const currentUserId = useSelector(getCurrentUserId);

  return (
    <div className="flex flex-row items-center justify-between gap-10 text-black text-md">
      <div className="flex flex-row items-center justify-between gap-3">
        <Checkbox id={colleague.id} checked={checked} value={colleague.id} onClick={() => onClick(colleague.id)} />
        <div className="flex flex-row items-center justify-between gap-2">
          {colleague.friendlyName}
          {currentUserId === colleague.id && (
            <Badge type="text" className="bg-secondary text-primary">
              Du
            </Badge>
          )}
        </div>
      </div>
      <div>{colleague.count}</div>
    </div>
  );
};

const TicketsFiltersMenu = () => {
  const [filtersMenuOpen, setFiltersMenuOpen] = useState(false);
  const { data, isFetching } = useGetColleaguesQuery();
  const colleaguesList = data?.data || [];
  const { data: currentUser } = useGetCurrentUser();

  const { etsTicketsfilters, setETSTicketsQueryParams } = useETSTicketsQueryParams();

  const creatorIdsArr = etsTicketsfilters.creatorIds ? etsTicketsfilters.creatorIds.split(',') : [];

  const onClick = (colleagueId: string) => {
    if (creatorIdsArr.includes(colleagueId)) {
      setETSTicketsQueryParams({
        creatorIds: creatorIdsArr.filter((c) => c !== colleagueId).join(','),
      });
      return;
    }
    setETSTicketsQueryParams({
      creatorIds: [...creatorIdsArr, colleagueId].join(','),
    });
  };

  return (
    <Popover open={filtersMenuOpen} onOpenChange={setFiltersMenuOpen}>
      <PopoverTrigger asChild>
        <div className="relative w-fit">
          {creatorIdsArr.length > 0 && (
            <NotificationBadge className="absolute end-0.5 top-1.5 size-2 rounded-full border-2 bg-red-11" />
          )}
          <IconButton
            Icon={FilterListFilled}
            iconSize="size-6"
            className="cursor-pointer"
            size="m"
            variant={filtersMenuOpen ? 'solid_neutral' : 'ghost_neutral'}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <div className="max-h-[40vh] overflow-y-auto p-3">
          {isFetching ? (
            <LoadingIndicator />
          ) : (
            <div className="flex flex-col gap-5">
              {colleaguesList
                .sort((a, b) => {
                  // Ensure the user is at the top
                  if (a.id === currentUser?.id) return -1;
                  if (b.id === currentUser?.id) return 1;

                  // Then sort by friendlyname
                  const nameA = a.friendlyName.toLowerCase();
                  const nameB = b.friendlyName.toLowerCase();
                  if (nameA < nameB) return -1;
                  if (nameA > nameB) return 1;

                  // If friendlynames are equal, sort by id
                  return a.id.localeCompare(b.id);
                })
                .map((c) => (
                  <Row key={c.id} colleague={c} onClick={onClick} checked={creatorIdsArr.includes(c.id)} />
                ))}
            </div>
          )}
        </div>

        <Separator />

        <Button
          type="button"
          variant={ButtonVariant.ghost_neutral}
          size={ButtonSize.xxl}
          className="w-full rounded-none"
          onClick={() => {
            setETSTicketsQueryParams({
              creatorIds: undefined,
            });
          }}
        >
          Filter löschen
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export default TicketsFiltersMenu;
